import { Decimal } from '@snap-types/types/decimal';

enum TransformStringToDecimalErrorEnum {
  EMPTY = 'EMPTY',
  NOT_A_NUMBER = 'NOT_A_NUMBER',
  NOT_AN_INTEGER = 'NOT_AN_INTEGER',
  NOT_A_POSITIVE_NUMBER = 'NOT_A_POSITIVE_NUMBER',
  NOT_NON_NEGATIVE_NUMBER = 'NOT_NON_NEGATIVE_NUMBER',
  NOT_NON_ZERO = 'NOT_NON_ZERO',
  GREATER_THAN_MAX = 'GREATER_THAN_MAX',
  LESS_THAN_MIN = 'LESS_THAN_MIN',
  INVALID = 'INVALID',
}

type Options = {
  allowDecimal?: boolean;
  allowNaN?: boolean;
  allowNegative?: boolean;
  allowZero?: boolean;
  max?: Decimal;
  min?: Decimal;
  customErrorMessages?: Partial<
    Record<TransformStringToDecimalErrorEnum, string>
  >;
};

const DEFAULT_ERROR_MESSAGES: Record<
  TransformStringToDecimalErrorEnum,
  string
> = {
  [TransformStringToDecimalErrorEnum.EMPTY]: 'Input is empty',
  [TransformStringToDecimalErrorEnum.NOT_A_NUMBER]: 'Input is not a number',
  [TransformStringToDecimalErrorEnum.NOT_AN_INTEGER]: 'Input is not an integer',
  [TransformStringToDecimalErrorEnum.NOT_A_POSITIVE_NUMBER]:
    'Input must be a positive number',
  [TransformStringToDecimalErrorEnum.NOT_NON_NEGATIVE_NUMBER]:
    'Input must not be a negative number',
  [TransformStringToDecimalErrorEnum.NOT_NON_ZERO]: 'Input cannot be 0',
  [TransformStringToDecimalErrorEnum.GREATER_THAN_MAX]:
    'Input must be less than or equal to max',
  [TransformStringToDecimalErrorEnum.LESS_THAN_MIN]:
    'Input must be greater than or equal to min',
  [TransformStringToDecimalErrorEnum.INVALID]: 'Input is invalid',
};

const defaultOptions = {
  allowNaN: false,
  allowNegative: false,
  allowZero: true,
  allowDecimal: true,
  customErrorMessages: {},
};

type Return =
  | {
      success: true;
      value: Decimal;
    }
  | {
      success: false;
      errorCode: TransformStringToDecimalErrorEnum;
      error: string;
    };

/**
 * @description
 * Transforms a string into a `Decimal` object according to the provided options.
 *
 * @param {string} value The string to be transformed.
 * @param {Options} [options={}] The options to be used for the transformation. Defaults to { allowNaN: false, allowNegative: false, allowZero: true, allowDecimal: true }.
 * @returns {Return} An object with the properties `success` and `error` or `value`.
 */
export function transformStringToDecimal(
  value: string,
  options: Options = {},
): Return {
  const {
    allowDecimal,
    allowNaN,
    allowNegative,
    allowZero,
    max,
    min,
    customErrorMessages,
  } = {
    ...defaultOptions,
    ...options,
  };

  const errorMessages = {
    ...DEFAULT_ERROR_MESSAGES,
    ...customErrorMessages,
  };

  if (!value.length) {
    return {
      success: false,
      errorCode: TransformStringToDecimalErrorEnum.EMPTY,
      error: errorMessages[TransformStringToDecimalErrorEnum.EMPTY],
    };
  }

  try {
    const decimal = new Decimal(value);

    if (!allowNaN && decimal.isNaN()) {
      return {
        success: false,
        errorCode: TransformStringToDecimalErrorEnum.NOT_A_NUMBER,
        error: errorMessages[TransformStringToDecimalErrorEnum.NOT_A_NUMBER],
      };
    }

    if (!allowDecimal && !decimal.isInteger()) {
      return {
        success: false,
        errorCode: TransformStringToDecimalErrorEnum.NOT_AN_INTEGER,
        error: errorMessages[TransformStringToDecimalErrorEnum.NOT_AN_INTEGER],
      };
    }

    if (!allowNegative) {
      if (!allowZero && decimal.lte(0)) {
        return {
          success: false,
          errorCode: TransformStringToDecimalErrorEnum.NOT_A_POSITIVE_NUMBER,
          error:
            errorMessages[
              TransformStringToDecimalErrorEnum.NOT_A_POSITIVE_NUMBER
            ],
        };
      }

      if (decimal.isNegative()) {
        return {
          success: false,
          errorCode: TransformStringToDecimalErrorEnum.NOT_NON_NEGATIVE_NUMBER,
          error:
            errorMessages[
              TransformStringToDecimalErrorEnum.NOT_NON_NEGATIVE_NUMBER
            ],
        };
      }
    }

    if (!allowZero && decimal.isZero()) {
      return {
        success: false,
        errorCode: TransformStringToDecimalErrorEnum.NOT_NON_ZERO,
        error: errorMessages[TransformStringToDecimalErrorEnum.NOT_NON_ZERO],
      };
    }

    if (max && decimal.gt(max)) {
      return {
        success: false,
        errorCode: TransformStringToDecimalErrorEnum.GREATER_THAN_MAX,
        error:
          errorMessages[TransformStringToDecimalErrorEnum.GREATER_THAN_MAX],
      };
    }

    if (min && decimal.lt(min)) {
      return {
        success: false,
        errorCode: TransformStringToDecimalErrorEnum.LESS_THAN_MIN,
        error: errorMessages[TransformStringToDecimalErrorEnum.LESS_THAN_MIN],
      };
    }

    return {
      success: true,
      value: decimal,
    };
  } catch {
    return {
      success: false,
      errorCode: TransformStringToDecimalErrorEnum.INVALID,
      error: errorMessages[TransformStringToDecimalErrorEnum.INVALID],
    };
  }
}
